import { useMemo } from 'react';
import { servicesForWarehouse, type OrderService } from '@mypoz/shared';

import { useOrderServices } from './orderServicesContext.js';

/**
 * What Step 3's services card shows (MYP-89): the services the order's warehouse performs, or why
 * there is no list to show.
 *
 * `kept` are services already on an order that its warehouse no longer offers — retired (live:
 * services 7 and 8, on 7 orders) or now listed under another warehouse. They stay on the order,
 * shown ticked and named from the order itself, so an edit of the remark does not quietly drop
 * them; the customer can untick one, but cannot add one.
 */
export type ServicesView =
  | { readonly kind: 'noWarehouse' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly retry: () => void }
  | {
      readonly kind: 'ready';
      readonly offered: readonly OrderService[];
      readonly kept: readonly { readonly id: string; readonly name: string }[];
    };

export function useScopedServices(
  warehouseId: string | undefined,
  keep: readonly { readonly id: string; readonly name: string }[] = NOTHING_KEPT,
): ServicesView {
  const { services, status, reload } = useOrderServices();

  return useMemo<ServicesView>(() => {
    if (warehouseId === undefined) return { kind: 'noWarehouse' };
    if (status === 'loading') return { kind: 'loading' };
    if (status === 'error') return { kind: 'error', retry: reload };

    const offered = servicesForWarehouse(services, warehouseId);
    const offeredIds = new Set(offered.map((service) => service.id));
    return { kind: 'ready', offered, kept: keep.filter((service) => !offeredIds.has(service.id)) };
  }, [warehouseId, status, reload, services, keep]);
}

const NOTHING_KEPT: readonly { readonly id: string; readonly name: string }[] = [];
